// =============================================================================
// ui/media/ScreenshotGrid.tsx — beta's N+1 tile grid, ported.
// -----------------------------------------------------------------------------
// One component for all three places beta calls `attachScreenshotControls`: the
// pre-market plan, the trade form and Best Opps. N filled 160×90 tiles plus a
// trailing dashed "Add screenshot" tile that is click-, keyboard-, paste- and
// drop-activatable — the four gestures beta wired, kept because a trader pastes
// from the clipboard and drags from Explorer in roughly equal measure.
//
// ── 🚨 IT DOES NOT PERSIST. THE OWNING FORM DOES. ──
//
// This is the one deliberate divergence from beta and it is a correctness one.
// Beta persists the whole journal on every paste (`onChange: persist`), which it
// can do because it has a single autosave for the entire entry. This client has
// per-writer save units, and a second writer touching `pre_market_plan` while a
// plan draft is open would (a) move `updated_at` and make the plan's own save
// hold as stale, and (b) be overwritten anyway the moment the draft saved, since
// `planToBlob` writes `draft.screenshots`. That is D61 exactly — a stale blank
// the user confirms is a fully authorised delete.
//
// So the grid takes `files` and returns `onChange(next)`. The upload to Storage
// happens immediately; the FILENAME lands wherever the owning form puts it and
// is written by that form's Save. The hint text says so, because "persists
// immediately" is beta's promise and would be a lie here.
//
// ── 🚨 REMOVE DE-REFERENCES, IT DOES NOT DELETE ──
//
// Matching beta, which splices the array and never calls a delete. Deliberate
// here as well: these buckets have NO BACKUP and no PITR (see media.ts), so an
// object removed by a misclick is gone permanently. An orphaned blob costs a few
// hundred KB; a deleted chart capture costs the trade review it was taken for.
//
// ── UPLOAD BEFORE COMMIT, AND A FAILURE IS LOUD ──
//
// The filename is only handed to `onChange` after the upload resolves. Beta
// pushes the name and uploads in the same breath, so a failed upload leaves a
// filename on the row pointing at nothing — a broken thumbnail with no error.
// =============================================================================

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { compressScreenshot } from '../../io/storage/compress';
import { mediaFilename, screenshotExt, uploadMedia } from '../../io/storage/media';
import { useMediaObjectUrl } from './useMediaObjectUrl';
import { Lightbox } from './Lightbox';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface ScreenshotGridProps {
  files: readonly string[];
  /** Filename stem. Beta uses `${date}-premarket`, `${date}-trade-${id}`. */
  prefix: string;
  onChange: (next: string[]) => void;
  /** Thumbnails and the viewer still work; add and remove are hidden. */
  readOnly?: boolean;
}

export function ScreenshotGrid({ files, prefix, onChange, readOnly = false }: ScreenshotGridProps) {
  const { sb } = useSupabase();
  const pickerId = useId();
  const pickerRef = useRef<HTMLInputElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);

  // ⚠ ARMED, not always-on. Beta's paste handler sits on `document` and would
  // otherwise steal every Ctrl+V in the app — including one aimed at the notes
  // textarea six inches below. Clicking the tile arms it; one paste disarms it.
  const [pasteArmed, setPasteArmed] = useState(false);

  // The list is read inside a document-level listener, so it goes through a ref.
  // Reading `files` from the closure would append to whatever list existed when
  // the listener was attached.
  const filesRef = useRef(files);
  filesRef.current = files;

  const add = useCallback(async (raw: Blob | null | undefined) => {
    if (!raw || !raw.type.startsWith('image/')) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await compressScreenshot(raw);
      const current = filesRef.current;
      const name = mediaFilename(prefix, current.length + 1, screenshotExt(blob.type));
      await uploadMedia(sb, 'screenshots', name, blob);
      onChange([...current, name]);
    } catch (e) {
      setError(reportFailure('upload screenshot', e, 'The upload failed.'));
    } finally {
      setBusy(false);
    }
  }, [sb, prefix, onChange]);

  useEffect(() => {
    if (!pasteArmed || readOnly) return;
    const onPaste = (e: ClipboardEvent) => {
      for (const item of e.clipboardData?.items ?? []) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          setPasteArmed(false);
          void add(item.getAsFile());
          return;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [pasteArmed, readOnly, add]);

  const remove = (i: number) => {
    setError(null);
    onChange(files.filter((_, n) => n !== i));
  };

  const activate = () => { setPasteArmed(true); pickerRef.current?.click(); };

  return (
    <div className="shot-grid-wrap">
      <div className="shot-grid" data-shots>
        {files.map((fn, i) => (
          <Thumb
            key={fn}
            filename={fn}
            readOnly={readOnly}
            onOpen={() => setViewing(i)}
            onRemove={() => remove(i)}
          />
        ))}

        {readOnly ? null : (
          <div
            className={'shot-tile empty' + (dragOver ? ' dragover' : '')}
            tabIndex={0}
            role="button"
            aria-label="Add screenshot"
            onClick={activate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void add(e.dataTransfer?.files?.[0]);
            }}
          >
            <div className="shot-tile-inner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>{busy ? 'Uploading…' : 'Add screenshot'}</span>
              <span className="hint">click · paste · drop</span>
            </div>
          </div>
        )}
      </div>

      {readOnly ? null : (
        <input
          ref={pickerRef}
          id={pickerId}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            // ⚠ Cleared so the SAME file can be picked again after a remove.
            e.target.value = '';
            void add(f);
          }}
        />
      )}

      {pasteArmed && !readOnly ? (
        <p className="muted shot-hint">Now paste with Ctrl+V.</p>
      ) : null}
      {error ? (
        <div role="alert" className="import-alert shot-hint">
          <b>Not attached.</b> {error}
        </div>
      ) : null}

      {viewing != null ? (
        <Lightbox files={files} startIndex={viewing} onClose={() => setViewing(null)} />
      ) : null}
    </div>
  );
}

function Thumb(
  { filename, readOnly, onOpen, onRemove }:
  { filename: string; readOnly: boolean; onOpen: () => void; onRemove: () => void },
) {
  const { url, error, loading } = useMediaObjectUrl('screenshots', filename);

  return (
    <div className={'shot-tile filled' + (url ? '' : ' shot-tile-blank')}>
      {url ? (
        // ⚠ A BUTTON, not a bare <img> with an onClick. Beta gets keyboard
        // access to the viewer for free from its delegated handler; here the
        // thumbnail has to be focusable itself or the gallery is mouse-only.
        <button type="button" className="shot-open" onClick={onOpen} title={filename}>
          <img className="screenshot" src={url} alt={filename} />
        </button>
      ) : (
        <div className="shot-tile-inner" title={error ?? filename}>
          <span className="hint">{loading ? 'Loading…' : 'Unavailable'}</span>
        </div>
      )}
      {readOnly ? null : (
        <button
          type="button" className="btn danger shot-rm"
          title={`Remove ${filename}`} aria-label={`Remove ${filename}`}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >×</button>
      )}
    </div>
  );
}
